import { useEffect, useState } from 'react';
import { api } from '../api';
import type { MemberPurchaseOptions, QuotaResource } from '../types';

export function quotaResourceForError(code: string): QuotaResource | null {
  if (code === 'processing_credit_exhausted') return 'processing';
  if (code === 'subtitle_credit_exhausted') return 'subtitle';
  if (code === 'storage_quota_exhausted') return 'storage';
  return null;
}

// Shared, on-demand help: never turn unrelated failures into a sales prompt.
export function QuotaHelp({ resource, onRefresh }: { resource: QuotaResource; onRefresh?: () => void | Promise<void> }) {
  const [options, setOptions] = useState<MemberPurchaseOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    setLoading(true);
    setOptions(null);
    setError('');
    void api.memberPurchaseOptions().then((next) => {
      if (active) setOptions(next);
    }, () => {
      if (active) setError('Unable to check top-up availability. Try again.');
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [resource, revision]);
  const option = options?.[resource];
  const heading = resource === 'storage' ? 'Need more playback storage?' : resource === 'processing'
    ? 'Need more processing minutes?' : 'Need more AI subtitle minutes?';
  return <div className="quota-help" aria-label={heading} aria-busy={loading}>
    <div className="quota-help__copy" role="status"><strong>{heading}</strong>
      <span>{loading ? 'Checking top-up availability…' : error || option?.unavailableReason || option?.detail}</span>
    </div>
    <div className="quota-help__actions">
      {!loading && !error && option?.href && <a className="button button--primary" href={option.href} target="_blank" rel="noopener noreferrer">{option.label}<span className="sr-only"> (opens checkout in a new tab)</span></a>}
      <button type="button" className="button button--secondary" disabled={loading} onClick={() => {
        setRevision((current) => current + 1);
        void onRefresh?.();
      }}>{error ? 'Try again' : onRefresh ? 'Refresh allowance' : 'Check availability'}</button>
    </div>
  </div>;
}
